import type { RootState } from '@/rootReducer';
import ReactDOM from 'react-dom';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { fromJS } from 'immutable';
import isEmpty from 'lodash/isEmpty';
import type { Map, Popup } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Loader, { Status } from '@/components/Loader/Loader';
import {
	Business,
	BusinessQuery,
	FilterQueryType,
	Search,
	SearchType
} from '@/modules/dashboard/dashboardTypes';
import './SearchBusinesses.scss';
import HeaderComponent from '@/components/HeaderRefactored/HeaderRefactored';
import type { BusinessCount } from '@/components/Table/Table';
import Button from '@/components/Button/newButtons';
import BusinessesList from '@/components/BusinessesList/businessesList';
import {
	dashboardUnselectBusinesses,
	dashboardSetQuery,
	dashboardExportBusinessesRequest,
	dashboardGetBusinessesRequest,
	dashboardClearBusinesses,
	dashboardSelectList,
	dashboardSelectBusiness,
	dashboardRemoveAllFromSaveList,
	dashboardAddManyToSaveList
} from '@/modules/dashboard/dashboardActions';
import { sendAnalytics } from '@/services/analyticsService';
import {
	PaperPlus,
	SearchHome,
	SearchBusinessesNoResults,
	LogoText2,
	Logo,
	OnboardingSequencesCampaign,
	OnboardingSearchBusiness,
	OnboardingSaveToList
} from '@/assets/images';
import Pagination from '@/components/Table/Pagination';
import {
	stateMapping,
	countryMapping
} from '@/components/HeaderRefactored/stateCountryMapping';
import { isQueryDifferent } from '@/modules/searchBusinesses/SearchBusinessesFilter/SearchTab/src/helpers/isQueryDifferent';
import { Tooltip } from '@/components/atoms/Tooltip';
import BusinessCard from './BusinessCard';
import SearchBusinessesMap from './SearchBusinessesMap';
import SearchBusinessesFilter from './SearchBusinessesFilter/SearchBusinessesFilter';
import { SearchBusinessesBusinessPreview } from './src/components/atoms/SearchBusinessesBusinessPreview';
import { UserRole } from '../user/userTypes';
import SaveToListManager from './SaveToListManager';

interface Props {
	listChange: (list: string) => void;
}

type SelectedView = 'businesses' | 'map';

const SearchBusinesses = ({ listChange }: Props): JSX.Element => {
	const isMapDisabledRef = useRef(false);
	const filtersClearedRef = useRef(true);

	const [
		query,
		businessSelected,
		businesses,
		businessesIsLoading,
		businessesCount,
		user,
		lists
	] = useSelector(
		(state: RootState) => [
			state.dashboard.get('query').toJS(),
			state.dashboard.getIn(['businesses', 'selected']).toJS(),
			state.dashboard.getIn(['businesses', 'data']).toJS(),
			state.dashboard.getIn(['businesses', 'isLoading']),
			state.dashboard.getIn(['businesses', 'count']),
			state.user.getIn(['authorize', 'user']).toJS(),
			state.dashboard.getIn(['lists', 'data']).toJS()
		],
		shallowEqual
	);
	const dispatch = useDispatch();

	const pageSize = 50;
	const defaultQuery = {
		minDistance: 0,
		maxDistance: 40,
		limit: 50,
		skip: 0,
		sortField: 'score',
		sort: { score: -1 },
		count: true
	};
	const pageName = 'search-businesses';

	const totalCount = isEmpty(businesses) ? 0 : businessesCount;
	const currentPage = query.skip / 50 + 1;
	const businessesData = useMemo(() => {
		const isDefaultQuery =
			query?.coordinates === null && query?.filters?.length === 0;

		if (isDefaultQuery) return [];

		return businesses.slice(0, 50).map(business => ({
			...business,
			_id: business._id || business.businessId
		}));
	}, [JSON.stringify(businesses), JSON.stringify(query)]);
	const queryChanged = isQueryDifferent(query);
	const hasBusinesses = queryChanged && businesses && businesses.length > 0;
	const isLocationSelected = query?.coordinates !== undefined;

	const setQuery = (q: BusinessQuery) =>
		dispatch(dashboardSetQuery({ ...q, user }));
	const selectList = (id: string) => dispatch(dashboardSelectList(id));
	const selectBusiness = (business: Business | null) =>
		dispatch(dashboardSelectBusiness(business));
	const getBusinesses = (q: BusinessQuery) =>
		dispatch(dashboardGetBusinessesRequest(q));
	const exportBusinessesRequest = (q: BusinessQuery) =>
		dispatch(dashboardExportBusinessesRequest(q));
	const removeAllFromSaveList = () =>
		dispatch(dashboardRemoveAllFromSaveList());
	const addManyToSaveList = (businessesList: Business[]) =>
		dispatch(dashboardAddManyToSaveList(businessesList));

	const [selectedView, setSelectedView] =
		useState<SelectedView>('businesses');
	const [loadingComplete, setLoadingComplete] = useState(false);
	const [checkboxList, setCheckboxList] = useState<{
		[key: string]: boolean;
	}>({});
	const [isQueryReset, setIsQueryReset] = useState(false);
	const [businessCount, setBusinessCount] = useState<BusinessCount>();
	const [openSaveToListModalButton, setOpenSaveToListModalButton] =
		useState<boolean>(false);
	const [openSaveToListModal, setOpenSaveToListModal] =
		useState<boolean>(false);
	const [tooltipShown, setTooltipShown] = useState(false);
	const [checkboxListData, setCheckboxListData] = useState<{
		[key: string]: boolean;
	}>({});
	const [saveToList, setSaveToList] = useState<Business[] | undefined>();
	const [largeListToSave, setLargeListToSave] = useState<
		Business[] | undefined
	>();
	const [userSortField, setUserSortField] = useState<{
		field: string;
		direction: number;
	}>({
		field: 'score',
		direction: -1
	});
	const [saveToListTriggered, setSaveToListTriggered] = useState(false);
	const [isCursorPagination, setIsCursorPagination] = useState(
		query.skip >= 10_000
	); // Production threshold (10,000) or 50 for testing

	const handleCheckboxListChange = (newCheckboxList: {
		[key: string]: boolean;
	}) => {
		setCheckboxListData(newCheckboxList);
	};

	const handleClearFilters = (cleared: boolean) => {
		filtersClearedRef.current = cleared;
		if (cleared) {
			// Clear all selected businesses when filters are reset
			handleRemoveAllChecks();
		}
		if (
			userSortField.direction !== 0 &&
			userSortField.field !== '' &&
			cleared
		) {
			setUserSortField({
				field: '', // Default field (no sorting)
				direction: 0 // Default direction (neutral sorting)
			});
		}

		if (!query.coordinates) {
			handleViewSwitch('businesses');
		}
	};

	const handleChangeIsCursorPagination = (value: boolean) => {
		setIsCursorPagination(value);
	};

	useEffect(() => {
		selectList('');
		dispatch(dashboardUnselectBusinesses());
		dispatch(dashboardClearBusinesses([])); // Clears the businesses list
		setQuery({
			...defaultQuery,
			coordinates: undefined,
			selectedLocation: undefined
		});
		setIsQueryReset(true);
	}, []);

	useEffect(() => {
		if (!isQueryReset) return;

		const { selectedLocation, state, country } = query || {};
		let shouldDisableMap =
			state in stateMapping || country in countryMapping;

		if (selectedLocation) {
			const locationArray = selectedLocation
				.split(',')
				.map((p: string) => p.trim());
			const isSingleLocationName = locationArray.length === 1;

			if (isSingleLocationName) {
				const locationName = locationArray[0];

				const isLocationNameInCountryMapping =
					locationName in countryMapping;

				shouldDisableMap = isLocationNameInCountryMapping;
			}

			const isTwoLocationNames = locationArray.length === 2;

			if (isTwoLocationNames) {
				const [firstLocationName, secondLocationName] = locationArray;

				const isSecondLocationNameInCountryMapping = Boolean(
					secondLocationName in countryMapping
				);

				const isLocationNameInStateAndCountryMapping = Boolean(
					firstLocationName in stateMapping &&
						secondLocationName in countryMapping
				);

				shouldDisableMap =
					isSecondLocationNameInCountryMapping ||
					isLocationNameInStateAndCountryMapping;
			}
		}

		isMapDisabledRef.current = shouldDisableMap;
	}, [query?.coordinates, query?.selectedLocation]);

	useEffect(() => {
		const queryNew: BusinessQuery = fromJS(query).toJS();
		const queryChanged = isQueryDifferent(queryNew);

		if (
			filtersClearedRef.current &&
			(queryNew.minYelpReviews === undefined ||
				queryNew.minYelpReviews === null) &&
			(queryNew.maxYelpReviews === undefined ||
				queryNew.maxYelpReviews === null)
		) {
			dispatch(dashboardClearBusinesses([])); // Clears the businesses list
			return; // Early return if filters are cleared
		}

		if (queryChanged) {
			// TODO: When radius changes to do not switch to business tab
			handleViewSwitch('businesses');
			if (!isCursorPagination) {
				delete queryNew.cursor;
			}

			// Set queryType to LOCATION if coordinates exist
			if (!queryNew.queryType) {
				queryNew.queryType = FilterQueryType.LOCATION;
			}

			// Handle searches (includes/excludes)
			if (queryNew.searches) {
				const includes: string[] = [];
				const excludes: string[] = [];

				queryNew.searches.forEach((search: Search) => {
					if (search.type === SearchType.INCLUDES) {
						includes.push(search.value);
					} else if (search.type === SearchType.EXCLUDES) {
						excludes.push(search.value);
					}
				});

				if (includes.length > 0) {
					queryNew.includes = JSON.stringify(includes);
				}

				if (excludes.length > 0) {
					queryNew.excludes = JSON.stringify(excludes);
				}

				delete queryNew.searches;
			}

			// If no fields are selected at this point, set default sort field
			if (userSortField?.field === '') {
				queryNew.sortField = 'score';
				queryNew.sort = {
					score: '-1'
				};
			}

			let { sortField } = queryNew;
			if (sortField === 'contacted') {
				sortField = 'isContacted';
			}

			// queryNew.sort = { [sortField]: queryNew.sort };
			delete queryNew.sortField;

			// Set limit based on listId presence
			queryNew.limit = 50;

			if (
				(queryNew.coordinates && !queryNew.selectedLocation) ||
				queryNew.listId
			) {
				return;
			}
			queryNew.count = true;

			// Fetch businesses if queryType is set correctly
			if (queryNew.queryType === FilterQueryType.EXPORT) {
				exportBusinessesRequest(queryNew);
			}
		}
	}, [JSON.stringify(query)]);

	const handleSelectBusiness = (business: Business) => {
		if (businessSelected._id && businessSelected._id === business._id) {
			return selectBusiness(null);
		}

		selectBusiness(business);

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: businessSelected._id
				? `Switched to another business in ${pageName}`
				: `Opened a business in ${pageName}`,
			properties: { businessId: business._id }
		});
	};

	const handleSelectBusinessGrouping = (grouping: Business[]) => {
		setLargeListToSave(grouping);
		addManyToSaveList(grouping);
		const updatedCheckboxes: Record<string, boolean> = {};
		grouping.forEach(b => {
			const businessId = b.businessId || b._id;
			updatedCheckboxes[businessId] = true;
		});
		setCheckboxListData(updatedCheckboxes);
		setCheckboxList(updatedCheckboxes);
	};

	const handleViewSwitch = (view: SelectedView) => {
		if (view === 'map') {
			setTooltipShown(true);
		}

		setSelectedView(view); // Update the selected view
	};

	useEffect(() => {
		const from = (currentPage - 1) * pageSize + 1;
		const to = from + businessesData.length - 1; // Ensure 'to' is based on available data length

		setBusinessCount({
			from,
			to,
			total: totalCount
		});
	}, [businessesData, totalCount, currentPage, pageSize]);

	const handleRemoveAllChecks = () => {
		const clearedState = {};
		setCheckboxList(clearedState);
		setCheckboxListData(clearedState); // Sync with BusinessesList
		setLargeListToSave([]);
		removeAllFromSaveList();
		// Clear the activated class from all checkboxes
		document.querySelectorAll('.activated').forEach(el => {
			el.classList.remove('activated');
		});
	};

	useEffect(() => {
		if (JSON.stringify(checkboxList) !== JSON.stringify(checkboxListData)) {
			setCheckboxList(checkboxListData || {});
		}
	}, [checkboxListData]);

	const handleCloseTooltip = () => {
		setTooltipShown(true);
	};

	useEffect(() => {
		if (!businessesIsLoading) {
			setLoadingComplete(true);
		} else {
			setLoadingComplete(false);
		}
	}, [businessesIsLoading]);

	useEffect(() => {
		const handleResize = () => {
			const isScreenWidthSmallerThen1024 = window.innerWidth < 1024;

			if (isScreenWidthSmallerThen1024) {
				handleViewSwitch('businesses');
			}
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const handleCloseSaveToListModal = () => {
		setOpenSaveToListModalButton(false);
	};

	return (
		<div className="relative flex h-auto w-full flex-col gap-4 overflow-auto p-4">
			<HeaderComponent
				icon={<SearchHome />}
				name="Search Businesses"
				description="Use advanced filters to search and connect with potential business leads."
				showSlider
				listName="TEST LIST NAME"
				selectedView={selectedView} // Pass selectedView as prop
				setSelectedView={handleViewSwitch} // Pass setSelectedView as prop
				showTooltip={
					isLocationSelected &&
					selectedView !== 'map' &&
					!tooltipShown
				} // Tooltip visibility controlled here
				isMapDisabled={isMapDisabledRef.current}
				onCloseTooltip={handleCloseTooltip} // Close tooltip logic here
			/>

			<div className="flex flex-1 flex-col gap-5 lg:flex-row">
				<SearchBusinessesFilter
					setFiltersCleared={handleClearFilters}
					handleSwitchView={handleViewSwitch}
					businessCount={businessCount?.total}
				/>

				<div className="flex h-full w-full flex-col gap-4">
					{/* Filter Header */}

					<header className="flex h-12 flex-col items-center justify-center gap-2 rounded-lg bg-dm-black p-2 md:flex-row md:justify-start md:gap-5 md:px-4">
						{businessCount && businessCount.total > 0 && (
							<>
								{Object.keys(checkboxListData).length > 0 && (
									<section className="flex items-center gap-1 md:gap-2">
										<button
											type="button"
											className="relative flex h-5 w-5 items-end rounded border-2 border-dm-white bg-dm-smart-blue"
											onClick={handleRemoveAllChecks}
										>
											<span className="absolute left-1/2 top-1/2 h-0.5 w-1/2 -translate-x-1/2 -translate-y-1/2 rounded bg-dm-white" />
										</button>

										<p className="whitespace-nowrap font-barlow text-sm font-semibold leading-none text-orange md:text-base">
											{
												Object.keys(checkboxListData)
													.length
											}{' '}
											selected
										</p>
									</section>
								)}

								<p className="whitespace-nowrap text-dm-white">
									{businessCount.from} - {businessCount.to} of{' '}
									{businessCount.total.toLocaleString()}{' '}
									businesses
								</p>

								<section className="relative inline-flex gap-4">
									<Tooltip.Provider>
										<Tooltip.Root>
											<Tooltip.Trigger asChild>
												<Button
													variant="primary"
													rightIcon={<PaperPlus />}
													disabled={
														Object.keys(
															businessesData
														).length === 0
													}
													onClick={() => {
														setOpenSaveToListModalButton(
															true
														);
													}}
												>
													Save to Prospecting List
												</Button>
											</Tooltip.Trigger>

											<Tooltip.Content
												align="start"
												side="bottom"
												disabled={
													Object.keys(businessesData)
														.length === 0
												}
												className="min-h-[5.75rem] max-w-[21.5rem] rounded-lg border border-dm-white bg-dm-blue px-4 py-2 align-middle font-barlow text-base font-normal leading-none text-dm-white"
											>
												This is your first step to
												prospect. Save selected
												businesses to a list, then reach
												out using our Compose Email or
												Sequence Campaigns features
											</Tooltip.Content>
										</Tooltip.Root>
									</Tooltip.Provider>

									<SaveToListManager
										isOpen={openSaveToListModalButton}
										onClose={() => {
											setSaveToList([]);
											setOpenSaveToListModalButton(false);
										}}
										largeListToSave={largeListToSave}
										query={query}
										saveToList={saveToList}
										setSaveToList={setSaveToList}
										onBusinessesSelected={
											handleSelectBusinessGrouping
										}
										removeAllChecks={handleRemoveAllChecks}
										listChange={listChange}
									/>
								</section>
							</>
						)}
					</header>

					<div className="relative flex flex-1 overflow-hidden">
						{businessesIsLoading && !loadingComplete ? (
							<div className="flex h-full w-full items-center justify-center fade-in">
								<Loader status={Status.LOADING} />
							</div>
						) : selectedView === 'businesses' ? (
							hasBusinesses ? (
								<>
									<BusinessesList
										user={user}
										businesses={businessesData}
										headers={[
											'Checkboxes',
											'Name',
											'RE2 Score',
											'Categories',
											'Locations',
											'Contacted'
										]}
										pageName="search-businesses"
										listChange={listChange}
										lists={lists}
										onCheckboxListChange={
											handleCheckboxListChange
										} // Pass the new prop
										userSortField={userSortField}
										setUserSortField={setUserSortField}
										saveToListTriggered={
											saveToListTriggered
										}
										resetSaveToListTrigger={() =>
											setSaveToListTriggered(false)
										}
										setOpenSaveToListModal={
											setOpenSaveToListModal
										}
										openSaveToListModal={
											openSaveToListModal
										}
										checkboxListData={checkboxListData}
										onSelectBusiness={handleSelectBusiness}
										largeListToSave={largeListToSave}
										setLargeListToSave={setLargeListToSave}
										onGetBusiness={getBusinesses}
									/>

									<SearchBusinessesBusinessPreview />
								</>
							) : queryChanged ? (
								<div className="no-results-container flex items-center justify-center px-14 py-8">
									<div className="no-results-bg-reset">
										<Logo
											style={{
												width: '245px',
												height: '56px'
											}}
										/>
										<div className="text-and-button-reset">
											<p className="background-text-top-reset">
												No businesses match your search
											</p>
											<p className="background-text-bottom-reset">
												{' '}
												Try adjusting your search or
												filters to find what you‘re
												looking for
											</p>
											<Button
												variant="primary"
												onClick={() => {
													sendAnalytics({
														type: 'track',
														userId: user.email,
														properties: {
															query
														},
														eventName:
															'User reset filter from no results'
													});
													setQuery({
														...defaultQuery,
														coordinates: undefined
													});
													selectBusiness(null);
												}}
											>
												Reset Filters
											</Button>
										</div>
									</div>
								</div>
							) : (
								// Default state: no businesses, no filters
								// Show no results SVG if no businesses are found
								<div className="flex h-fit w-full flex-col gap-6 rounded-lg border border-dm-mid-grey bg-dm-black px-7 py-4 md:h-full md:px-14 md:py-8">
									<span className="text-left text-3xl font-bold text-dm-mid-grey md:text-4xl">
										Getting started with Resquared
									</span>

									<div className="flex h-full flex-wrap items-center justify-center gap-4 px-4 xl:flex-nowrap">
										<OnboardingSearchBusiness className="h-auto max-h-[12.5rem] w-auto max-w-full object-contain lg:max-h-[15.625rem] xl:max-h-none" />
										<OnboardingSaveToList className="h-auto max-h-[12.5rem] w-auto max-w-full object-contain lg:max-h-[15.625rem] xl:max-h-none" />
										<OnboardingSequencesCampaign className="h-auto max-h-[12.5rem] w-auto max-w-full object-contain lg:max-h-[15.625rem] xl:max-h-none" />
									</div>

									<span className="inline-flex flex-wrap items-center justify-center gap-[1ch]">
										<p className="whitespace-nowrap leading-6">
											Learn more about
										</p>

										<LogoText2 className="block h-6 w-auto" />

										<p className="whitespace-nowrap leading-6">
											in
										</p>

										<a
											href="https://learn.re2.ai/"
											target="_blank"
											rel="noopener noreferrer"
											className="whitespace-nowrap text-base font-semibold leading-6 text-white"
										>
											Knowledge Base
										</a>
									</span>
								</div>
							)
						) : (
							<MapView
								isLocationSelected={isLocationSelected}
								selectedView={selectedView}
								onViewSwitch={handleViewSwitch}
								onBusinessSelected={handleSelectBusiness}
							/>
						)}
					</div>

					<BusinessFooter
						isVisible={
							hasBusinesses &&
							((selectedView === 'map' && query?.coordinates) ||
								selectedView === 'businesses')
						}
						canPreviousPage={query.skip > 0}
						canNextPage={businessesData.length / 50 >= 1}
						businessCount={businessCount}
						currentPage={currentPage}
						pageSize={pageSize}
						totalCount={totalCount}
						isCursorPagination={isCursorPagination}
						onChangeIsCursorPagination={
							handleChangeIsCursorPagination
						}
						isExportMenuVisible={
							businessesData.length > 0 &&
							user &&
							user.role !== UserRole.Free &&
							user.role !== UserRole.PreTrial
						}
					/>
				</div>
			</div>
		</div>
	);
};

type MapViewProps = {
	isLocationSelected: boolean;
	selectedView: SelectedView;
	onViewSwitch: (view: SelectedView) => void;
	onBusinessSelected: (business: Business) => void;
};

const MapView = ({
	isLocationSelected,
	selectedView,
	onViewSwitch,
	onBusinessSelected
}: MapViewProps) => {
	const mapBoxRef = useRef<HTMLDivElement>(null);
	const currentPopup: Popup | null = null;

	const mapInstance = useRef<Map | null>(null);
	const mapStateRef = useRef<{ zoom: number; center: [number, number] }>({
		zoom: 8,
		center: [0, 0]
	});

	const previousCoordinates = useRef<string | null>(null);

	const gotoBusinessView = async (business: Business) => {
		// Check if the business is in the current dataset
		let businessIndex = businesses.findIndex(
			b => b.id === business._id || b._id === business._id
		);

		// Wait for the businesses to load
		await new Promise(resolve => setTimeout(resolve, 500)); // Adjust timeout as needed

		// Check if the business is now available
		businessIndex = businesses.findIndex(
			b => b.id === business._id || b._id === business._id
		);

		if (businessIndex === -1) {
			console.error(
				`Business with ID ${business._id} not found after fetching.`
			);
			return;
		}

		onViewSwitch('businesses');
		onBusinessSelected(business);

		const container = document.querySelector(
			'.search-businesses-items-list'
		);
		const businessElement = document.querySelector(
			`[data-business-id="${business._id}"]`
		);

		if (container && businessElement) {
			// Scroll the container to the business element
			const containerTop = container.getBoundingClientRect().top;
			const elementTop = businessElement.getBoundingClientRect().top;

			// Calculate the scroll position to bring the element into view
			const offset = elementTop - containerTop + container.scrollTop;

			container.scrollTo({
				top: offset,
				behavior: 'smooth'
			});

			// Add highlight class to the element
			businessElement.classList.add('highlight');

			// Remove the highlight after 2 seconds
		} else {
			console.warn(
				`Container or business element for ID ${business._id} not found.`
			);
		}
	};

	const createPopupContent = (business, onClose) => {
		const popupBusiness = businesses.find(
			({ _id }) => _id === business._id
		);

		const container = document.createElement('div');
		ReactDOM.render(
			<BusinessCard
				business={business}
				onClose={onClose}
				user={user}
				onTabClick={() => {
					gotoBusinessView(popupBusiness);
					sendAnalytics({
						type: 'track',
						userId: user.email,
						properties: {
							businessId: business._id
						},
						eventName:
							'User clicked on business card to go to business view'
					});
				}} // Updated handler
			/>,
			container
		);
		return container;
	};

	const {
		query,
		businessSelected,
		businesses,
		businessesIsLoading,
		businessesCount,
		user,
		mapBoxKey,
		cursors,
		lists
	} = useSelector(
		(state: RootState) => ({
			query: state.dashboard.get('query').toJS(),
			businessSelected: state.dashboard
				.getIn(['businesses', 'selected'])
				.toJS(),
			businesses: state.dashboard.getIn(['businesses', 'data']).toJS(),
			businessesIsLoading: state.dashboard.getIn([
				'businesses',
				'isLoading'
			]),
			businessesCount: state.dashboard.getIn(['businesses', 'count']),
			user: state.user.getIn(['authorize', 'user']).toJS(),
			mapBoxKey: state.dashboard.getIn(['map', 'key']),
			cursors:
				state.dashboard.getIn(['businesses', 'cursors'])?.toJS() || [],
			lists: state.dashboard.getIn(['lists', 'data']).toJS()
		}),
		shallowEqual
	);

	const dispatch = useDispatch();

	const setQuery = (q: BusinessQuery) =>
		dispatch(dashboardSetQuery({ ...q, user }));

	if (!isLocationSelected) {
		return (
			<div className="no-results-container">
				<div className="no-results-bg">
					<SearchBusinessesNoResults />
					<p className="background-text">
						Your background message here
					</p>
				</div>
			</div>
		);
	}

	return (
		<SearchBusinessesMap
			selectedView={selectedView}
			query={query}
			businesses={businesses}
			mapBoxKey={mapBoxKey}
			setQuery={setQuery}
			createPopupContent={createPopupContent}
			mapBoxRef={mapBoxRef}
			mapInstance={mapInstance}
			currentPopup={currentPopup}
			mapStateRef={mapStateRef}
			previousCoordinates={previousCoordinates}
		/>
	);
};

type BusinessFooterProps = {
	isVisible: boolean;
	businessCount: BusinessCount | undefined;
	onChangeIsCursorPagination: (value: boolean) => void;
	isCursorPagination: boolean;
	pageSize: number;
	currentPage: number;
	totalCount: number;
	canPreviousPage: boolean;
	canNextPage: boolean;
	isExportMenuVisible: boolean;
};

const BusinessFooter = ({
	isVisible,
	businessCount,
	onChangeIsCursorPagination,
	isCursorPagination,
	pageSize,
	currentPage,
	totalCount,
	canPreviousPage,
	canNextPage,
	isExportMenuVisible
}: BusinessFooterProps) => {
	const { query, user, cursors } = useSelector(
		(state: RootState) => ({
			query: state.dashboard.get('query').toJS(),
			user: state.user.getIn(['authorize', 'user']).toJS(),
			cursors:
				state.dashboard.getIn(['businesses', 'cursors'])?.toJS() || []
		}),
		shallowEqual
	);

	const dispatch = useDispatch();

	const setQuery = useCallback(
		(q: BusinessQuery) => dispatch(dashboardSetQuery({ ...q, user })),
		[dispatch, user]
	);
	const getBusinesses = useCallback(
		(q: BusinessQuery) => dispatch(dashboardGetBusinessesRequest(q)),
		[dispatch]
	);
	const selectBusiness = useCallback(
		(business: Business | null) =>
			dispatch(dashboardSelectBusiness(business)),
		[dispatch]
	);

	const handleGotoPage = useCallback(
		(page: number) => {
			const queryNew: BusinessQuery = {
				...query,
				skip: page * 50,
				count: true,
				cursor:
					page * 50 >= 10000 && cursors[page]
						? cursors[page]
						: query.cursor,
				queryType:
					(query.queryType === FilterQueryType.EXPORT ||
						!query.queryType) &&
					query.queryTypePrev
						? query.queryTypePrev
						: query.queryType,
				sort:
					query.sortField === 'distance'
						? { distance: '1' }
						: query.sort
			};

			onChangeIsCursorPagination(queryNew.skip >= 10000);

			setQuery(queryNew);
			getBusinesses(queryNew);
			selectBusiness(null);
		},
		[
			cursors,
			getBusinesses,
			onChangeIsCursorPagination,
			query,
			selectBusiness,
			setQuery
		]
	);

	const handleNextPage = useCallback(
		(currentPage: number) => {
			const queryNew: BusinessQuery = {
				...query,
				skip: currentPage * 50,
				count: true,
				cursor:
					isCursorPagination && cursors[currentPage]
						? cursors[currentPage]
						: query.cursor,
				queryType:
					(query.queryType === FilterQueryType.EXPORT ||
						!query.queryType) &&
					query.queryTypePrev
						? query.queryTypePrev
						: query.queryType
			};

			onChangeIsCursorPagination(queryNew.skip >= 101);

			setQuery(queryNew);
			getBusinesses(queryNew);
			selectBusiness(null);
		},
		[
			cursors,
			getBusinesses,
			isCursorPagination,
			onChangeIsCursorPagination,
			query,
			selectBusiness,
			setQuery
		]
	);

	const handlePreviousPage = useCallback(
		(currentPage: number) => {
			const queryNew: BusinessQuery = {
				...query,
				skip: (currentPage - 2) * 50,
				count: true,
				cursor:
					isCursorPagination && cursors[currentPage - 2]
						? cursors[currentPage - 2]
						: query.cursor,
				queryType:
					(query.queryType === FilterQueryType.EXPORT ||
						!query.queryType) &&
					query.queryTypePrev
						? query.queryTypePrev
						: query.queryType
			};

			setQuery(queryNew);
			getBusinesses(queryNew);
			selectBusiness(null);
		},

		[
			cursors,
			getBusinesses,
			isCursorPagination,
			query,
			selectBusiness,
			setQuery
		]
	);

	const exportBusinesses = useCallback(
		contactedOnly => {
			const queryNew: BusinessQuery = fromJS(query).toJS();

			if (queryNew.queryType !== FilterQueryType.EXPORT) {
				if (queryNew.queryType) {
					queryNew.queryTypePrev = queryNew.queryType;
				}

				queryNew.queryType = FilterQueryType.EXPORT;
				queryNew.onlyContactedForExport = contactedOnly;
			}

			if (setQuery) {
				setQuery(queryNew);
			}

			if (queryNew.listId && user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'User exported contacted emails from list',
					properties: {
						listId: queryNew.listId
					}
				});
			} else if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'User exported contacted emails from location'
				});
			}
		},
		[JSON.stringify(query), setQuery]
	);

	if (!isVisible) return null;

	return (
		<footer className="search-businesses-footer flex-col md:flex-row">
			<section className="footer-pagination">
				{businessCount && businessCount.total > 50 && (
					<Pagination
						previousPage={handlePreviousPage}
						canPreviousPage={canPreviousPage}
						currentPage={currentPage}
						totalPages={Math.ceil(totalCount / pageSize)}
						gotoPage={handleGotoPage}
						nextPage={handleNextPage}
						canNextPage={canNextPage}
						cursorPagination={isCursorPagination}
						user={user}
					/>
				)}
			</section>

			<section className="footer-buttons">
				{isExportMenuVisible && (
					<div className="export-button-container">
						<Button variant="text" className="export-button">
							Export (first 250 results)
						</Button>

						{/* Export Menu */}
						<div className="export-dropdown">
							<button
								type="button"
								className="export-dropdown-item"
								onClick={() => exportBusinesses(true)}
							>
								Export information for first 250 businesses
								you&apos;ve contacted
								<br />
								<span className="export-subtext">
									(email addresses included)
								</span>
							</button>

							<button
								type="button"
								className="export-dropdown-item"
								onClick={() => exportBusinesses(false)}
							>
								Export information for first 250 businesses
								<br />
								<span className="export-subtext">
									(email addresses excluded)
								</span>
							</button>
						</div>
					</div>
				)}
			</section>
		</footer>
	);
};

export default SearchBusinesses;
